import { createFileRoute, Outlet, useNavigate, useRouterState, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { requireAdmin } from "@/lib/admin-auth.functions";
import {
  LogOut,
  LayoutDashboard,
  CalendarRange,
  BarChart3,
  Truck,
  Users,
  Car,
  FileText,
  Building2,
  Handshake,
  Map,
  Webhook,
  Menu,
  X,
  PanelLeft,
  Mail,
  FileEdit,
} from "lucide-react";

export const Route = createFileRoute("/admin")({
  component: AdminLayout,
});

type NavItem = { to: string; label: string; icon: typeof LayoutDashboard; exact?: boolean };
type NavGroup = { label?: string; items: NavItem[] };

const NAV: NavGroup[] = [
  {
    items: [
      { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
      { to: "/admin/offers", label: "Offers", icon: CalendarRange },
    ],
  },
  {
    label: "Fleet Management",
    items: [
      { to: "/admin/quality-metrics", label: "Quality Metrics", icon: BarChart3 },
      { to: "/admin/fleets", label: "Fleets", icon: Truck },
      { to: "/admin/drivers", label: "Drivers", icon: Users },
      { to: "/admin/vehicles", label: "Vehicles", icon: Car },
    ],
  },
  {
    label: "Organization",
    items: [
      { to: "/admin/invoices", label: "Invoices", icon: FileText },
      { to: "/admin/company-profile", label: "Company Profile", icon: Building2 },
    ],
  },
  {
    label: "Network",
    items: [
      { to: "/admin/suppliers", label: "Suppliers", icon: Building2 },
      { to: "/admin/partners", label: "Partners", icon: Handshake },
      { to: "/admin/city-coverages", label: "City Coverages", icon: Map },
    ],
  },
  {
    label: "Content",
    items: [{ to: "/admin/articles", label: "Blog & Articles", icon: FileEdit }],
  },
  {
    label: "Webhooks",
    items: [{ to: "/admin/event-logs", label: "Event logs", icon: Webhook }],
  },
];

const APP_VERSION = "2026.06.04.1445";

function AdminLayout() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [state, setState] = useState<"checking" | "ready" | "anon" | "forbidden">("checking");
  const [email, setEmail] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const checkAdmin = useServerFn(requireAdmin);

  useEffect(() => {
    let mounted = true;
    const verify = async (userEmail: string | null) => {
      try {
        const res = await checkAdmin();
        if (!mounted) return;
        setEmail(userEmail);
        setState(res.ok ? "ready" : "forbidden");
      } catch {
        if (!mounted) return;
        setState("forbidden");
      }
    };
    const check = async () => {
      const { data } = await supabase.auth.getUser();
      if (!mounted) return;
      if (data.user) {
        await verify(data.user.email ?? null);
      } else {
        setState("anon");
      }
    };
    check();
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      if (!mounted) return;
      if (session?.user) {
        verify(session.user.email ?? null);
      } else {
        setEmail(null);
        setState("anon");
      }
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, [checkAdmin]);

  // Close mobile drawer on navigation
  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  if (pathname === "/admin/login") return <Outlet />;

  if (state === "checking") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-sm text-muted-foreground">Loading admin…</div>
      </div>
    );
  }

  if (state === "anon") {
    navigate({ to: "/admin/login", replace: true });
    return null;
  }

  if (state === "forbidden") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center">
        <h1 className="font-display text-2xl text-foreground">Access denied</h1>
        <p className="max-w-sm text-sm text-muted-foreground">
          This admin area is restricted. The signed-in account ({email}) is not authorised.
        </p>
        <button
          onClick={async () => {
            await supabase.auth.signOut();
            navigate({ to: "/admin/login", replace: true });
          }}
          className="rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          Sign out
        </button>
      </div>
    );
  }

  const isActive = (to: string, exact?: boolean) =>
    exact ? pathname === to : pathname === to || pathname.startsWith(to + "/");

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate({ to: "/admin/login", replace: true });
  };

  const Sidebar = ({ onNavigate }: { onNavigate?: () => void }) => (
    <div className="flex h-full w-64 flex-col bg-background border-r border-border">
      <div className="px-5 py-5 border-b border-border">
        <Link to="/admin" onClick={onNavigate} className="flex items-baseline gap-1">
          <span className="font-display text-xl tracking-tight text-foreground">Bacha Way</span>
          <span className="text-xs font-semibold text-muted-foreground">Travel</span>
        </Link>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
        {NAV.map((group, gi) => (
          <div key={gi} className="space-y-1">
            {group.label && (
              <div className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                {group.label}
              </div>
            )}
            {group.items.map(({ to, label, icon: Icon, exact }) => {
              const active = isActive(to, exact);
              return (
                <Link
                  key={to}
                  to={to}
                  onClick={onNavigate}
                  className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                    active
                      ? "bg-accent/15 text-accent font-medium"
                      : "text-foreground/80 hover:bg-muted hover:text-foreground"
                  }`}
                >
                  <Icon className={`h-4 w-4 ${active ? "text-accent" : "text-muted-foreground"}`} />
                  {label}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>
      <div className="border-t border-border px-4 py-3 text-xs space-y-1">
        <div className="flex items-center gap-1.5 text-foreground font-medium">
          <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
          Bacha Way Travel SARL
        </div>
        <div className="text-muted-foreground truncate">Signed in as {email ?? "—"}</div>
        <div className="text-muted-foreground">Version: {APP_VERSION}</div>
        <button
          onClick={handleLogout}
          className="mt-2 w-full rounded-md border border-border px-3 py-1.5 text-foreground hover:bg-muted"
        >
          Log out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-muted/30 flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:flex-shrink-0 lg:sticky lg:top-0 lg:h-screen">
        <Sidebar />
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute inset-y-0 left-0 h-full shadow-xl">
            <Sidebar onNavigate={() => setDrawerOpen(false)} />
          </div>
          <button
            onClick={() => setDrawerOpen(false)}
            aria-label="Close menu"
            className="absolute top-4 right-4 rounded-full bg-background p-2 text-foreground shadow"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border bg-background px-4 py-3 lg:px-6">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setDrawerOpen(true)}
              className="lg:hidden rounded-md border border-border p-1.5 text-foreground hover:bg-muted"
              aria-label="Open menu"
            >
              <Menu className="h-4 w-4" />
            </button>
            <button
              className="hidden lg:inline-flex rounded-md p-1.5 text-muted-foreground hover:bg-muted"
              aria-label="Toggle sidebar"
            >
              <PanelLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-medium text-foreground">Admin Portal</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Connected
            </span>
            <button className="rounded-md p-1.5 text-muted-foreground hover:bg-muted" aria-label="Inbox">
              <Mail className="h-4 w-4" />
            </button>
          </div>
        </header>

        <main className="flex-1 px-4 py-5 lg:px-8 lg:py-7">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
